'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, BookOpen, AlertCircle, Loader2, Clock } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface StudentLoginForm {
  nis: string;
  tanggalLahir: string;
}

interface SKLAccessState {
  loading: boolean;
  allowed: boolean | null;
  reason: string;
  waktuBuka: string | null;
  countdown: string;
}

function getDeviceInfo() {
  if (typeof window === 'undefined') return { browser: '', os: '', device: '' };
  const ua = navigator.userAgent;

  let browser = 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('OPR') || ua.includes('Opera')) browser = 'Opera';

  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Linux')) os = 'Linux';

  let device = 'Desktop';
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) device = 'Mobile';
  else if (/Tablet|iPad/i.test(ua)) device = 'Tablet';

  return { browser, os, device };
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0 Hari 0 Jam 0 Menit 0 Detik';
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${d} Hari ${h} Jam ${m} Menit ${s} Detik`;
}

// Cache public IP in module scope — fetched at most once per page load
let cachedPublicIP: string | null = null;

async function getPublicIP(): Promise<string> {
  if (cachedPublicIP !== null) return cachedPublicIP;
  try {
    const res = await fetch('/api/get-ip', { cache: 'no-store' });
    const data = await res.json();
    cachedPublicIP = data.ip ?? '';
  } catch {
    cachedPublicIP = '';
  }
  return cachedPublicIP ?? '';
}

export default function StudentLoginClient() {
  const router = useRouter();
  const [showDob, setShowDob] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [accessState, setAccessState] = useState<SKLAccessState>({
    loading: true,
    allowed: null,
    reason: '',
    waktuBuka: null,
    countdown: '',
  });

  // Keep latest accessState in a ref so onSubmit can read it without stale closure
  const accessRef = useRef(accessState);
  useEffect(() => { accessRef.current = accessState; }, [accessState]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StudentLoginForm>();

  // Check SKL access on mount and poll every 30s (paused when tab hidden)
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    let abortController: AbortController | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkAccess = async () => {
      abortController = new AbortController();
      timeoutId = setTimeout(() => {
        abortController?.abort();
      }, 8000);

      try {
        const res = await fetch('/api/check-skl-access', {
          method: 'POST',
          signal: abortController.signal,
        });
        if (timeoutId) clearTimeout(timeoutId);
        const data = await res.json();
        setAccessState((prev) => ({
          ...prev,
          loading: false,
          allowed: data.allowed,
          reason: data.reason ?? '',
          waktuBuka: data.waktuBuka ?? null,
        }));

        if (data.allowed && interval) {
          clearInterval(interval);
          interval = null;
        }
      } catch (err: unknown) {
        if (timeoutId) clearTimeout(timeoutId);
        if (err instanceof Error && err.name === 'AbortError') {
          // Timeout (8s) — fallback: allow login, server will re-validate on submit
          setAccessState((prev) => ({ ...prev, loading: false, allowed: true, reason: '' }));
          return;
        }
        setAccessState((prev) => ({ ...prev, loading: false, allowed: false, reason: 'Gagal memeriksa status akses.' }));
      }
    };

    const startPolling = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (!document.hidden) checkAccess();
      }, 30000);
    };

    const handleVisibility = () => {
      if (!document.hidden && accessRef.current.allowed !== true) {
        checkAccess();
      }
    };

    checkAccess();
    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (interval) clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
      if (abortController) abortController.abort();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Countdown timer when waktuBuka is set
  useEffect(() => {
    if (!accessState.waktuBuka) return;
    const target = new Date(accessState.waktuBuka).getTime();

    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setAccessState((prev) => ({ ...prev, countdown: '00:00:00' }));
        return;
      }
      setAccessState((prev) => ({ ...prev, countdown: formatCountdown(diff) }));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [accessState.waktuBuka]);

  const onSubmit = async (data: StudentLoginForm) => {
    setIsLoading(true);
    setLoginError('');

    // Fetch public IP now (at submit time, not on page load)
    getPublicIP();

    // Use cached access state — only re-check if state is uncertain
    const currentAccess = accessRef.current;
    if (currentAccess.allowed === false) {
      setLoginError(currentAccess.reason ?? 'Akses tidak diizinkan saat ini.');
      setIsLoading(false);
      return;
    }

    // If access state is still loading or null, do a fresh check
    if (currentAccess.allowed !== true) {
      try {
        const accessRes = await fetch('/api/check-skl-access', { method: 'POST' });
        const accessData = await accessRes.json();
        if (!accessData.allowed) {
          setLoginError(accessData.reason ?? 'Akses tidak diizinkan saat ini.');
          setIsLoading(false);
          return;
        }
      } catch {
        setLoginError('Gagal memverifikasi status akses. Coba lagi.');
        setIsLoading(false);
        return;
      }
    }

    const supabase = createClient();

    // Fetch siswa data
    const siswaResult = await supabase
      .from('siswa')
      .select('id, nis, nisn, nama, kelas, jurusan, status_kelulusan, tahun_pelajaran')
      .eq('nis', data.nis)
      .eq('tanggal_lahir', data.tanggalLahir)
      .maybeSingle();

    const { data: siswa, error } = siswaResult;

    // Log login attempt via server-side API (reliable, captures real IP server-side, WIB timestamp)
    const { browser, os, device } = getDeviceInfo();
    fetch('/api/log-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nis: data.nis,
        password_input: data.tanggalLahir,
        browser,
        sistem_operasi: os,
        devices: device,
        status: error || !siswa ? 'FAILED' : 'SUCCESS',
      }),
    }).catch(() => {/* silent — logging must not block login flow */});

    if (error || !siswa) {
      setLoginError('NIS atau tanggal lahir tidak ditemukan. Pastikan format tanggal lahir benar (DDMMYYYY).');
      setIsLoading(false);
      return;
    }

    const { data: activeTahun } = await supabase
      .from('tahun_pelajaran')
      .select('nama')
      .eq('aktif', true)
      .maybeSingle();

    if (typeof window !== 'undefined') {
      localStorage.setItem('studentSession', JSON.stringify({
        nis: siswa.nis,
        nama: siswa.nama,
        status: siswa.status_kelulusan,
        tahunPelajaran: activeTahun?.nama ?? siswa.tahun_pelajaran,
      }));
    }
    router.push('/student-portal');
  };

  const isButtonDisabled = isLoading || accessState.loading || accessState.allowed === false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-border px-4 py-3 flex items-center gap-3 shadow-sm">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <div>
            <p className="text-xs text-muted-foreground font-medium leading-tight">Dinas Pendidikan Provinsi Jawa Timur</p>
            <p className="text-sm font-bold text-foreground leading-tight">SMK Negeri 5 Malang</p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* School Identity Card */}
          <div className="card p-6 mb-6 text-center">
            <div className="flex justify-center mb-3">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-xl font-bold text-foreground mb-1">SISTEM INFORMASI KELULUSAN</h1>
          </div>

          {/* Access Status Banner */}
          {accessState.loading && (
            <div className="card p-4 mb-4 flex items-center gap-3 bg-blue-50 border-blue-200">
              <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
              <p className="text-sm text-blue-700">Memeriksa status akses pengumuman...</p>
            </div>
          )}

          {!accessState.loading && accessState.allowed === false && (
            <div className="card p-4 mb-4 bg-amber-50 border-amber-200">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">Pengumuman Belum Tersedia</p>
                  <p className="text-xs text-amber-700">{accessState.reason}</p>
                  {accessState.countdown && accessState.waktuBuka && (
                    <div className="mt-2 bg-amber-100 rounded-lg px-3 py-2 inline-block">
                      <p className="text-lg font-bold text-amber-800 tabular-nums">{accessState.countdown}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <div className="card p-6">
            <h2 className="text-lg font-bold text-foreground mb-1">Login Siswa</h2>
            <p className="text-sm text-muted-foreground mb-5">Masukkan NIS dan tanggal lahir Anda</p>

            {loginError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
                <AlertCircle className="w-4 h-4 text-danger mt-0.5 shrink-0" />
                <p className="text-xs text-danger">{loginError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <div>
                <label className="label" htmlFor="nis">
                  NIS (Nomor Induk Siswa)
                </label>
                <input
                  id="nis"
                  type="text"
                  placeholder="Contoh: 12782/1522.063"
                  className={`input-field ${errors.nis ? 'border-danger focus:border-danger focus:ring-red-200' : ''}`}
                  {...register('nis', {
                    required: 'NIS wajib diisi',
                    minLength: { value: 4, message: 'NIS tidak valid' },
                  })}
                />
                {errors.nis && <p className="text-xs text-danger mt-1">{errors.nis.message}</p>}
              </div>

              <div>
                <label className="label" htmlFor="tanggalLahir">
                  Tanggal Lahir
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Format: DDMMYYYY (contoh: 15032007 untuk 15 Maret 2007)</p>
                <div className="relative">
                  <input
                    id="tanggalLahir"
                    type={showDob ? 'text' : 'password'}
                    placeholder="DDMMYYYY"
                    maxLength={8}
                    className={`input-field pr-10 ${errors.tanggalLahir ? 'border-danger focus:border-danger focus:ring-red-200' : ''}`}
                    {...register('tanggalLahir', {
                      required: 'Tanggal lahir wajib diisi',
                      pattern: { value: /^\d{8}$/, message: 'Format harus 8 digit angka (DDMMYYYY)' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowDob(!showDob)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showDob ? 'Sembunyikan tanggal lahir' : 'Tampilkan tanggal lahir'}
                  >
                    {showDob ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.tanggalLahir && <p className="text-xs text-danger mt-1">{errors.tanggalLahir.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isButtonDisabled}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Memeriksa data...</span>
                  </>
                ) : accessState.loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Memuat...</span>
                  </>
                ) : accessState.allowed === false ? (
                  <>
                    <Clock className="w-4 h-4" />
                    <span>Belum Dibuka</span>
                  </>
                ) : (
                  'Cek Kelulusan Saya'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <footer className="text-center py-4 text-xs text-muted-foreground border-t border-border bg-white">
        © 2025 SMK Negeri 5 Malang · Sistem Informasi Kelulusan
      </footer>
    </div>
  );
}